import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';

const ResultCard = ({ image, index, onClick }) => {
  return (
    <div
      onClick={() => onClick(index)}
      className="relative group cursor-pointer rounded-2xl overflow-hidden
        transform transition-all duration-300 hover:scale-102 hover:shadow-2xl"
    >
      {/* Image */}
      <div className="relative aspect-square bg-gray-50">
        <img
          src={image.url}
          alt={image.label}
          className="w-full h-full object-contain"
        />
        {/* Hover overlay */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-all duration-300" />
      </div>
    </div>
  );
};

export const ResultsView = ({
  preview,
  images,
  onOpenModal,
  onOrder,
  onCreateAnother,
  onCancel
}) => {
  const [showConfetti, setShowConfetti] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);

  // Show confetti animation on mount
  useEffect(() => {
    setShowConfetti(true);
    const timer = setTimeout(() => setShowConfetti(false), 3000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="max-w-2xl mx-auto">
      {/* Confetti Effect */}
      {showConfetti && (
        <div className="fixed inset-0 pointer-events-none z-50">
          <div className="confetti-container">
            {[...Array(50)].map((_, i) => (
              <div
                key={i}
                className="confetti"
                style={{
                  left: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 3}s`,
                  backgroundColor: ['#FF6B6B', '#4ECDC4', '#FFE66D', '#95E1D3'][
                    Math.floor(Math.random() * 4)
                  ]
                }}
              />
            ))}
          </div>
        </div>
      )}

      <div className="mb-8">
        <h1 className="text-4xl font-bold text-gray-900 mb-4 text-center">
          LaLala Model Maker
        </h1>
      </div>

      <div className="bg-white rounded-2xl shadow-xl p-8">
        {/* Thumbnail Bar */}
        <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-200">
          <button
            onClick={() => setShowPreviewModal(true)}
            className="flex items-center space-x-3 hover:bg-gray-50 rounded-lg p-2 transition-colors"
          >
            <img
              src={preview}
              alt="Your photo"
              className="h-12 w-12 rounded-lg object-cover cursor-pointer"
            />
            <span className="text-sm text-gray-600">Tap to view</span>
          </button>
          <button
            onClick={onCancel}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            title="Start over"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Main Content */}
        <div className="space-y-8 py-8">
          {/* Title */}
          <div className="text-center">
            <h2 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-red-500 mb-2">
              ✨ Your Model is Ready! ✨
            </h2>
          </div>

          {/* Image Grid - Two images side by side */}
          {images && images.length > 0 ? (
            <div className="grid grid-cols-2 gap-6">
              {images.map((image, index) => (
                <ResultCard
                  key={index}
                  image={image}
                  index={index}
                  onClick={onOpenModal}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-16 bg-gray-50 rounded-2xl">
              <p className="text-gray-500">Processing...</p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="space-y-3 pt-4">
            <button
              onClick={onOrder}
              className="w-full h-16 text-xl font-bold text-white rounded-xl shadow-lg
                bg-gradient-to-r from-red-500 to-orange-500
                hover:from-red-600 hover:to-orange-600
                transition-all duration-300"
            >
              🛒 Order Now
            </button>

            <button
              onClick={onCreateAnother}
              className="w-full h-12 text-sm text-gray-600 hover:text-gray-900
                bg-gray-50 hover:bg-gray-100 rounded-xl transition-colors"
            >
              ➕ Create Another
            </button>
          </div>
        </div>
      </div>

      {/* Preview Modal */}
      {showPreviewModal && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center"
          onClick={() => setShowPreviewModal(false)}
        >
          <button
            onClick={() => setShowPreviewModal(false)}
            className="absolute top-4 right-4 z-50 p-3 bg-white/10 hover:bg-white/20
              rounded-full transition-colors backdrop-blur-sm"
            title="Close"
          >
            <X className="w-8 h-8 text-white" />
          </button>

          <div className="absolute top-4 left-4 z-50 bg-black/50 px-6 py-3 rounded-2xl backdrop-blur-sm">
            <h3 className="text-white text-xl font-bold">Your Original Photo</h3>
          </div>

          <div className="relative w-full h-full flex items-center justify-center p-4">
            <img
              src={preview}
              alt="Your original photo"
              className="max-w-full max-h-[80vh] object-contain"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}

      <style>{`
        .confetti-container {
          position: relative;
          width: 100%;
          height: 100%;
        }

        .confetti {
          position: absolute;
          width: 10px;
          height: 10px;
          opacity: 0;
          animation: confetti-fall 3s ease-in forwards;
        }

        @keyframes confetti-fall {
          0% {
            opacity: 1;
            transform: translateY(-100px) rotate(0deg);
          }
          100% {
            opacity: 0;
            transform: translateY(100vh) rotate(720deg);
          }
        }
      `}</style>
    </div>
  );
};
